import type React from "react";
import { CompanyVendorSelector } from "~/components/common/company_vendors/CompanyVendorSelector";
import { DealCancelButtonClient } from "~/components/common/deals/show/main/DealCancelButtonClient";
import { DealMainWorkflowClientCancel } from "~/components/common/deals/show/main/DealMainWorkflowClientCancel";
import { DealClientTodoTable } from "~/components/common/deals/show/main/todos/DealClientTodoTable";
import { StatusBar } from "~/components/common/parts/status/StatusBar";
import { DealSurveyAnswers } from "~/components/specifics/deals/[id]/deal_survey/Answers";
import { StatusSwitcher } from "~/components/specifics/deals/[id]/statuses/StatusSwitcher";
import { DealContext } from "~/context/DealContext";
import { useDealSurveyAnswers } from "~/hooks/deals/useDealSurveyAnswers";
import { useDeals } from "~/hooks/useDeals";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { useStatuses } from "~/hooks/useStatuses";
import { isWithVendorDeal } from "~/models/Deal";
import { getOriginDealVendor } from "~/models/DealVendorCompany";
import {
	getOwnStatuses,
	getWithVendorStatuses,
	isCancelApplying,
	isCanceled,
	isCompleted,
	NOW_ACCEPTING
} from "~/models/Status";
import Styles from "./DealMainWorkflow.module.scss";

export const DealMainWorkflowClient: React.FC = () => {
	const id = useIdQueryHandler();
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const { data: statuses } = useStatuses().index();
	const { data: answers } = useDealSurveyAnswers().index(id);
	if (statuses === null || deal === null) {
		return null;
	}
	const originVendor = getOriginDealVendor(deal);
	const displayStatuses = isWithVendorDeal(deal)
		? getWithVendorStatuses(statuses)
		: getOwnStatuses(statuses);

	if (isCancelApplying(deal)) {
		return (
			<DealMainWorkflowClientCancel
				companyVendor={originVendor?.companyVendor ?? null}
				deal={deal}
				displayStatuses={displayStatuses}
			/>
		);
	}

	return (
		<DealContext.Provider value={{ deal, mutateDeal }}>
			<div>
				{!isCanceled(deal.statusId) && (
					<div className={Styles.StatusCard}>
						<h2 className={Styles.Header}>
							{originVendor === null ? (
								<span className={Styles.NotYet}>
									まだ施工会社が選択されていません
								</span>
							) : (
								<span>
									{originVendor?.companyVendor?.name}
									{!isCompleted(deal.statusId) && "に依頼中"}
								</span>
							)}
							{!isCompleted(deal.statusId) && <DealCancelButtonClient />}
						</h2>
						<StatusBar currentId={deal.statusId} statuses={displayStatuses} />
					</div>
				)}
				<div className={Styles.TodoCard}>
					<h3 className={Styles.TodoCardTitle}>TODO</h3>
					<div className={Styles.TodoCardContents}>
						<DealClientTodoTable />
					</div>
				</div>
				{answers !== null && answers.length !== 0 && (
					<div className={Styles.SurveyAnswerCard}>
						<DealSurveyAnswers answers={answers} />
					</div>
				)}
				{deal.statusId === NOW_ACCEPTING && (
					<div className={Styles.SelectDealVendorCompanyCard}>
						<div>
							<h3 className={Styles.Header}>
								工事を依頼する施工会社を選択してください
							</h3>
							<CompanyVendorSelector
								deal={deal}
								key={JSON.stringify({
									dealId: deal.id,
									estimationBy: deal.estimationBy,
									savedCompanyVendorId: deal.dealVendorCompanies[0] ?? null,
									savedShowTenant:
										deal.dealVendorCompanies[0]?.showTenant ?? false
								})}
								mutateDeal={mutateDeal}
							/>
						</div>
					</div>
				)}
				<div className={Styles.WorkflowCard}>
					<StatusSwitcher statusId={deal.statusId} />
				</div>
			</div>
		</DealContext.Provider>
	);
};
